import type * as arrow from "apache-arrow";
import { sortDateColumns } from "../../utils/date";
import type { RowStatus, SkuRow } from "./types";

export interface MaterializeOpts {
  /** Period column names, in order (drives the per-period measure arrays). */
  periodColumns: string[];
  /** The sku column name for the current grain (GRAIN_TO_SKU_COL[skuGrain]). */
  skuCol: string;
  /** Current display grain — controls vividly_product / contributing-sku handling. */
  skuGrain: "shipping" | "vividly" | "stick";
}

// Dimension/identity columns to exclude when detecting period (date) columns —
// the period columns are the date-formatted ones left over. Kept here (not in
// RetailerSupplyView) so the parquet worker and the main thread compute the
// SAME period set/order: the worker materializes measure arrays in this order
// and the main thread indexes them by the same order. A divergence would
// misalign every per-period value.
const PERIOD_SKIP_COLS = new Set([
  "retailer", "shipping_sku", "vividly_product", "stick_sku", "sku_name",
  "measure", "status", "status_updated_at", "ship_to_location", "first_receiver",
  "planning_level", "target_weeks_of_supply", "contributing_shipping_skus",
  "ioh_source",
  // CHO-678 Phase C: per-group attribute columns on the lean summary VTs
  // (collapsed rows). Not period columns — keep them out of the time axis.
  "sku", "pl_count", "_has_inventory_data", "_has_sales_forecast",
]);

/** Period (date) columns in chronological order, from the VT column-name list. */
export function computePeriodColumns(columns: string[]): string[] {
  return sortDateColumns(
    columns.filter(
      (c) =>
        !PERIOD_SKIP_COLS.has(c) &&
        !c.startsWith("promo:") &&
        !c.startsWith("edited:") &&
        !c.startsWith("price:"),
    ),
  );
}

/**
 * Opaque post-process descriptor carried through the generic parquet parse path
 * (parquet.ts / worker-manager / worker) so the worker can materialize SkuRow[]
 * in the same pass it parses — no second Arrow IPC round-trip (CHO-678 Phase 7
 * Step 2, re-architected). Only `ui_supply_walk_for_editing` sets it.
 */
export interface SkuRowsPostProcess {
  kind: "skuRows";
  skuCol: string;
  skuGrain: "shipping" | "vividly" | "stick";
}

export interface SkuRowsPostProcessResult {
  skuRows: SkuRow[];
  /** Worker-side materialization time (ms) — for the offload benchmark. */
  materializeMs: number;
}

/**
 * Run the skuRows post-process against a freshly-parsed table inside the parquet
 * worker. Computes the period columns from the table's own column names (so it
 * matches the main thread's `periodColumns`) and materializes.
 */
export function runSkuRowsPostProcess(
  table: arrow.Table,
  columns: string[],
  pp: SkuRowsPostProcess,
): SkuRowsPostProcessResult {
  const t0 = performance.now();
  const skuRows = materializeSkuRows(table, {
    periodColumns: computePeriodColumns(columns),
    skuCol: pp.skuCol,
    skuGrain: pp.skuGrain,
  });
  return { skuRows, materializeMs: Math.round(performance.now() - t0) };
}

/**
 * Arrow table → SkuRow[] materialization for the planner console.
 *
 * Extracted verbatim from RetailerSupplyView's `allSkuRows` memo (CHO-678
 * Phase 7 Step 2) so the SAME logic runs both in the derived-state worker and
 * on the main-thread fallback — no risk of the two diverging. The heavy part is
 * the ~9M Arrow `col.get(i)` reads over 75k rows × ~130 cols; running it in a
 * worker keeps the main thread responsive (the result is a few thousand grouped
 * rows, cheap to structured-clone back).
 *
 * Pure: depends only on (table, opts). Must stay behavior-identical to the
 * original memo — see the null-preservation and grain notes inline.
 */
export function materializeSkuRows(
  table: arrow.Table,
  opts: MaterializeOpts,
): SkuRow[] {
  const { periodColumns, skuCol, skuGrain } = opts;
  if (!table || table.numRows === 0 || periodColumns.length === 0) return [];

  const grouped = new Map<
    string,
    {
      firstReceiver: string;
      planningLevel: string;
      sku: string;
      skuName: string;
      vividlyProduct: string | null;
      contributingShippingSkus: string[];
      targetWOS: number | null;
      iohSource: string | null;
      measures: Record<string, (number | null)[]>;
      promotions: (string | null)[];
      editedAt: (string | null)[];
      unitPriceShipping: (number | null)[];
      status: RowStatus;
      statusUpdatedAt: string | null;
    }
  >();

  const isRowStatus = (v: string): v is RowStatus =>
    v === "baseline" || v === "edited" || v === "ready";

  // Hoist column-name lookups out of the row loop. table.getChild(name) is
  // O(numColumns) — calling it inside the row loop made this O(rows * cols^2)
  // and pinned the main thread for 8-10s on dc-grain runs.
  const colFirstReceiver = table.getChild("first_receiver");
  const colPlanningLevel = table.getChild("planning_level");
  const colSku = table.getChild(skuCol);
  const colSkuName = table.getChild("sku_name");
  const colVividlyProduct = table.getChild("vividly_product");
  const colContribShippingSkus = table.getChild("contributing_shipping_skus");
  const colTargetWOS = table.getChild("target_weeks_of_supply");
  const colIohSource = table.getChild("ioh_source");
  const colMeasure = table.getChild("measure");
  const colStatus = table.getChild("status");
  const colStatusUpdatedAt = table.getChild("status_updated_at");
  const colPeriods = periodColumns.map((c) => table.getChild(c));
  const colPromos = periodColumns.map((c) => table.getChild(`promo:${c}`));
  const colEdits = periodColumns.map((c) => table.getChild(`edited:${c}`));
  const colPrices = periodColumns.map((c) => table.getChild(`price:${c}`));

  for (let i = 0; i < table.numRows; i++) {
    const firstReceiver = String(colFirstReceiver?.get(i) ?? "");
    // CHO-478 stage 3: planning_level falls back to first_receiver when the
    // VT didn't emit it (legacy parquets pre-CHO-478 ship_to_mapping change).
    // Buckets 1 + 3 always have planning_level == first_receiver, so the
    // fallback is correct for those; bucket 2 needs a current run to surface
    // its DC drilldown.
    const planningLevelRaw = colPlanningLevel?.get(i);
    const planningLevel =
      planningLevelRaw != null && String(planningLevelRaw).length > 0
        ? String(planningLevelRaw)
        : firstReceiver;
    const sku = String(colSku?.get(i) ?? "");
    const skuName = String(colSkuName?.get(i) ?? sku);
    // vividly_product is only carried as an extra dim at shipping grain;
    // at the vividly grain it IS the sku, at stick grain it's dropped.
    const vpRaw = skuGrain === "shipping"
      ? colVividlyProduct?.get(i)
      : skuGrain === "vividly"
        ? sku
        : null;
    const vividlyProduct = vpRaw != null ? String(vpRaw) : null;
    // At non-shipping grain the VT attaches the set of shipping_skus that
    // roll up into this row, comma-joined. Used for cascade-clear.
    const contribRaw = colContribShippingSkus?.get(i);
    const contributingShippingSkus =
      skuGrain === "shipping"
        ? [sku]
        : contribRaw != null && String(contribRaw).length > 0
          ? String(contribRaw).split(",")
          : [];
    const twosRaw = colTargetWOS?.get(i);
    const targetWOS = twosRaw != null && Number(twosRaw) > 0 ? Number(twosRaw) : null;
    const iohSourceRaw = colIohSource?.get(i);
    const iohSource = iohSourceRaw != null && String(iohSourceRaw).length > 0 ? String(iohSourceRaw) : null;
    const measure = String(colMeasure?.get(i) ?? "");
    // Preserve null from the arrow column (don't coerce to 0). The engine
    // emits null for measures suppressed under CHO-610 (starting_inventory,
    // actual_sales, ending_inventory, ending_days_of_supply on
    // netsuite_fallback rows). Carrying the null through to the aggregate
    // / cell-render layer lets us show a dash instead of a misleading "0".
    const values: (number | null)[] = colPeriods.map((col) => {
      const raw = col?.get(i);
      if (raw == null) return null;
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    });
    // Row identity keys on planning_level. Buckets 1 + 3 collapse to FR
    // (planning_level == first_receiver, one row per (FR, sku)); bucket 2
    // splits into per-DC rows (planning_level == "<FR> - <DC>"). FR is in
    // the key as a defensive guard against stray planning_level collisions
    // across FRs from a data-quality regression — strictly redundant
    // otherwise.
    const key = `${planningLevel}::${firstReceiver}::${sku}`;

    if (!grouped.has(key)) {
      const promotions = colPromos.map((col) => {
        const raw = col?.get(i);
        if (raw == null) return null;
        const str = String(raw).trim();
        return str.length > 0 ? str : null;
      });
      const editedAt = colEdits.map((col) => {
        const raw = col?.get(i);
        if (raw == null) return null;
        const str = String(raw).trim();
        return str.length > 0 ? str : null;
      });
      // CHO-459 follow-up: per-cell unit_price_shipping. The viewtransform
      // pivots it into price:MM/dd/yy columns; we read it here so the FE
      // recalc can update forecast_shipment_revenue live on planner edits.
      const unitPriceShipping = colPrices.map((col) => {
        const raw = col?.get(i);
        if (raw == null) return null;
        const n = Number(raw);
        return Number.isFinite(n) ? n : null;
      });
      const statusRaw = colStatus?.get(i);
      const statusStr = statusRaw != null ? String(statusRaw) : "baseline";
      const status: RowStatus = isRowStatus(statusStr) ? statusStr : "baseline";
      const suaRaw = colStatusUpdatedAt?.get(i);
      const statusUpdatedAt = suaRaw != null && String(suaRaw).length > 0 ? String(suaRaw) : null;
      grouped.set(key, {
        firstReceiver, planningLevel, sku, skuName, vividlyProduct,
        contributingShippingSkus, targetWOS, iohSource, measures: {}, promotions,
        editedAt, unitPriceShipping, status, statusUpdatedAt,
      });
    }
    grouped.get(key)!.measures[measure] = values;
  }

  return [...grouped.entries()].map(([key, data]) => ({
    id: key,
    firstReceiver: data.firstReceiver,
    planningLevel: data.planningLevel,
    sku: data.sku,
    skuName: data.skuName,
    vividlyProduct: data.vividlyProduct,
    contributingShippingSkus: data.contributingShippingSkus,
    targetWOS: data.targetWOS,
    iohSource: data.iohSource,
    measures: data.measures,
    promotions: data.promotions,
    editedAt: data.editedAt,
    unitPriceShipping: data.unitPriceShipping,
    status: data.status,
    statusUpdatedAt: data.statusUpdatedAt,
  }));
}
